import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User, Mail, AlertCircle, CheckCircle, MessageCircle } from 'lucide-react';

interface UserProfile {
  id: string;
  full_name: string | null;
  email: string;
  telegram_id: string;
}

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState<UserProfile>({
    id: '',
    full_name: '',
    email: '',
    telegram_id: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      setProfile({
        id: user.id,
        full_name: profileData?.full_name || '',
        email: user.email || '',
        telegram_id: profileData?.telegram_id ? String(profileData.telegram_id) : ''
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const telegramIdValue = profile.telegram_id.trim()
        ? parseInt(profile.telegram_id.trim(), 10)
        : null;

      const { error: updateError } = await supabase
        .from('profiles')
        .upsert({
          id: profile.id,
          full_name: profile.full_name,
          telegram_id: telegramIdValue,
          updated_at: new Date().toISOString()
        });

      if (updateError) throw updateError;

      setSuccess('Perfil actualizado correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      [e.target.id]: e.target.value
    });
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Mi Perfil</h1>
          <p className="mt-2 text-gray-400">
            Gestiona tu información personal
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-green-500">{success}</p>
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email
              </label>
              <div className="mt-1 relative">
                <input
                  type="email"
                  id="email"
                  value={profile.email}
                  disabled
                  className="input-field pl-10 opacity-50 cursor-not-allowed"
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-300">
                Nombre Completo
              </label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  id="full_name"
                  value={profile.full_name || ''}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="Tu nombre completo"
                />
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="telegram_id" className="block text-sm font-medium text-gray-300">
                ID de Telegram
              </label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  id="telegram_id"
                  value={profile.telegram_id}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="Ej: 123456789"
                  inputMode="numeric"
                />
                <MessageCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Para vincular tu cuenta de Telegram: busca{' '}
                <span className="text-primary font-mono">@ICT_Trading_AssistantBot</span>, escribe{' '}
                <span className="text-primary font-mono">/start</span> y el bot te enviará tu ID.
                Copia ese número aquí y guarda.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;